// Junior Science Curriculum Data - Years 7-10
// Based on Vanuatu Basic Science Syllabus

export const juniorScienceCurriculum = {
  subject: "Junior Basic Science",
  years: "7-10",
  totalHours: {
    year7: 102,
    year8: 102,
    year9: 93,
    year10: 59
  },
  rationale: `Science teaching in Vanuatu aims to promote scientific knowledge and understanding relevant to students' lives as citizens of Vanuatu, providing a sound basis for further studies in science. The curriculum emphasizes practical skills, problem-solving, and responsible attitudes towards the environment.`,
  
  themes: [
    {
      code: "A",
      name: "Energy and the Transfer of Energy",
      description: "Including forces, heat, light, sound, electricity, electromagnetism, and chemical energy"
    },
    {
      code: "B",
      name: "Materials and Their Reactions",
      description: "Including air and water, nature and variety of substances, chemical change, and particulate nature of matter"
    },
    {
      code: "C",
      name: "Living Things and Their Interactions",
      description: "Including plants and animals, cells, basic structures and life processes, basic human biology, health and hygiene"
    },
    {
      code: "D",
      name: "The Nature of the Earth",
      description: "Including rocks, landscape, volcanism, and earthquakes"
    }
  ],
  
  assessment: {
    practical: {
      weighting: 30,
      description: "Internal assessment of practical science skills based on short tests administered during Year 10"
    },
    written: {
      weighting: 70,
      paper1: {
        name: "Multiple Choice",
        time: "1 hour",
        marks: 40,
        content: "40 multiple-choice items testing knowledge (20 items) and understanding (20 items)"
      },
      paper2: {
        name: "Structured Questions",
        time: "1.5 hours",
        marks: 60,
        content: "Questions of various lengths testing knowledge (20 marks), understanding (25-30 marks), and problem-solving (10-15 marks)"
      }
    }
  },

  years: {
    year7: {
      hours: 102,
      focus: "Developing and sustaining curiosity about the world, enjoyment of scientific activity, and understanding of natural phenomena through inquiry-based learning",
      strands: [
        {
          id: "strand1",
          title: "Science in Action",
          description: "Conduct scientific investigations using an inquiry methodology, including collecting and analysing data; developing scientific explanations; using evidence to form defensible conclusions and providing solutions to real-world problems.",
          majorLearningOutcome: "Students will be able to conduct scientific investigations using an inquiry methodology.",
          subStrands: [
            {
              id: "1.1",
              title: "Questioning and Predicting",
              description: "Identify questions that can be investigated scientifically and make predictions about results based on scientific knowledge.",
              slos: [
                "Identify scientifically testable questions",
                "Formulate a question to be investigated within the content of the Science Understanding sub-strands",
                "Predict results or outcomes of an investigation question",
                "Identify problems/issues that can be investigated at a local level that will provide data for addressing regional problems or issues",
                "Construct questions from observations and other data about local challenges"
              ]
            },
            {
              id: "1.2",
              title: "Planning and Conducting",
              description: "Plan and conduct investigations, including fieldwork and fair experiments involving two situations or variables.",
              slos: [
                "Use a variety of investigation methods, including fieldwork, to collect data",
                "Select and use the most appropriate equipment that enables accurate and consistent collection of data",
                "Identify the independent, dependent, and controlled variables in an investigation",
                "Manipulate and measure variables during the investigation",
                "Make and record clear and accurate observations",
                "Identify safety considerations and apply them while conducting the investigation",
                "Identify and mitigate any ethical dilemmas that may occur during data collection",
                "Collect enough data to gauge reliability of measurements",
                "Use preliminary trials to test and improve procedures"
              ]
            },
            {
              id: "1.3",
              title: "Processing and Analysing",
              description: "Use tables for the systematic recording of data and represent data in simple graphs; observe patterns and trends in the data.",
              slos: [
                "Construct appropriate tables to record the collected data",
                "Use the appropriate type of graph to display the data",
                "Outline trends or patterns in the data as shown in the graphs",
                "Identify data that support the hypothesis or answers the question, and data that does not",
                "Summarise the data and attempt to explain the patterns",
                "Write conclusions based on the evidence from the investigation"
              ]
            },
            {
              id: "1.4",
              title: "Evaluating",
              description: "Suggest improvements to the methods of data collection to increase accuracy of their own work and evaluate the work of others.",
              slos: [
                "Recognise inconsistencies in data (i.e. outliers, or unexpected results)",
                "Describe the difficulties in taking accurate and consistent measurements",
                "Summarise the problems encountered in collecting the data, including external influences",
                "Suggest the use of different equipment for more accurate and consistent data collection"
              ]
            },
            {
              id: "1.5",
              title: "Communicating",
              description: "Use scientific literacy skills to communicate their findings and ideas.",
              slos: [
                "Use the correct scientific terminology appropriately in explanations",
                "Use international conventions in presenting data (e.g. SI units, graphs and tables)",
                "Use different formats to present findings to an audience, including models, diagrams, digital presentations, and actions",
                "Present the results of an investigation to different communities and groups for consideration",
                "Recognise bias in communications"
              ]
            }
          ]
        },
        {
          id: "strand2",
          title: "Science Understanding",
          description: "Understand key ideas of Science across the sub-strands, including facts, concepts, principles, laws, theories and models established by scientists over time.",
          majorLearningOutcome: "Students will understand key ideas of Science and the importance of Science in contemporary decision-making and problem-solving.",
          subStrands: [
            {
              id: "2.1",
              title: "Biological Science: Living Things in Their Environment",
              description: "Investigate and organise information about the diversity of life; observe interactions of organisms in the environment; investigate how traditional agricultural practices and local environments are affected by natural cycles or events.",
              slos: [
                "Identify the diversity of living things in local communities",
                "Identify producers, consumers, and decomposers and explain their roles in food chains and webs",
                "Describe the relationships between organisms and between organisms and their environment",
                "Relate the locations or activities of living things and the features of the environment",
                "Explain the impact of changing populations of organisms on a local food web",
                "Describe the impact of the introduction of feral animals or weeds into a local environment",
                "Suggest and justify a control program for a named, introduced pest species",
                "Summarise the impact of physical changes in the environment such as seasonal changes, earthquakes, or volcanoes",
                "Explain how traditional agricultural practices and local environments are affected by natural cycles or events"
              ]
            },
            {
              id: "2.2",
              title: "Chemical Science: Mixtures and Pure Substances",
              description: "Investigate the differences between pure substances and mixtures, solutions as homogeneous mixtures; investigate methods of separating components of mixtures including provision of safe, clean drinking water.",
              slos: [
                "List situations where mixtures, including solutions and pure substances, are used in everyday life",
                "Describe a pure substance as being made up of one type of substance chemically combined, and a mixture as being made of different kinds of substances physically combined",
                "Distinguish between the solvent and solute in a solution",
                "Explain concentration of solutions in terms of the amount of solute compared to the amount of solvent",
                "Describe the physical separation techniques of filtration, decantation, evaporation, crystallisation, and distillation and give an example of when you would use them",
                "Summarise a range of separation techniques and where they might be used in everyday life today and in traditional Vanuatu culture",
                "Illustrate how the properties of materials in mixtures can be used to separate a mixture into its components",
                "Explain how, using a combination of separation techniques, drinkable water can be produced from dirty, salty, or polluted sources"
              ]
            },
            {
              id: "2.3",
              title: "Earth and Space Science: Sun, Moon and Earth System",
              description: "Investigate and use models of the relationships between the Sun, Moon and Earth to predict and explain cycles and events; explore traditional cultural stories of these cycles and events.",
              slos: [
                "Use models or other representations to show the relative positions of the Sun, Moon, and Earth to observe different phases of the Moon",
                "Use models and other representations to show the relative positions of the Sun, Moon, and Earth leading to solar, lunar, partial, and total eclipses",
                "Outline how seasons are largely due to factors surrounding the Earth's tilted axis as it revolves around the Sun",
                "Record evidence for changes in the Sun's position in the sky during the day and the year",
                "Summarise the differences between seasons related to the weather experienced at different times of the year and in different places",
                "Relate the relative positions of the Sun and Moon to the timing of tidal movements of the ocean",
                "Explain how seasons and tides influence the local environments and human activity",
                "Describe the significance of phases of the Moon, eclipses, and seasonal changes in traditional Vanuatu culture"
              ]
            },
            {
              id: "2.4",
              title: "Physical Science: Forces and Their Effects",
              description: "Investigate the effects of interactions of gravity and friction on movement of objects; explore the influence of mass, shape and surface features on movement; investigate the role of friction and gravity in everyday life.",
              slos: [
                "Identify when forces are acting by changes in shape or motion of objects",
                "Describe how the force of gravity between objects is related to their masses and their distance apart",
                "Describe the weight of an object as the force of gravity acting on that object",
                "Use diagrams or models to show forces acting on an object, including gravity and friction",
                "Describe friction as a force acting between surfaces in contact",
                "Explain how friction can be used to control the motion of objects",
                "Outline how friction can be increased or decreased to improve the workings and safety of a machine",
                "Give examples where the shapes of animals have overcome friction related to their movement",
                "Explain the importance of friction in everyday life"
              ]
            }
          ]
        }
      ]
    },
    year8: {
      hours: 102,
      focus: "Developing scientific inquiry skills and understanding key concepts in biological, chemical, earth and physical sciences relevant to Vanuatu",
      strands: [
        {
          id: "strand1",
          title: "Science in Action",
          description: "Conduct scientific investigations using an inquiry methodology, including collecting and analysing data; developing scientific explanations; using evidence to form defensible conclusions and providing solutions to real-world problems.",
          majorLearningOutcome: "Students will be able to conduct scientific investigations using an inquiry methodology.",
          subStrands: [
            {
              id: "1.1",
              title: "Questioning and Predicting",
              description: "Identify questions that can be investigated scientifically and make predictions about results based on scientific knowledge.",
              slos: [
                "Identify scientifically testable questions about local phenomena",
                "Formulate investigation questions within the content of the Science Understanding sub-strands",
                "Predict results or outcomes of an investigation based on prior knowledge",
                "Identify problems or issues that can be investigated at a local level to provide data for addressing regional challenges",
                "Construct questions from observations and other data about local environmental and community issues"
              ]
            },
            {
              id: "1.2",
              title: "Planning and Conducting",
              description: "Plan and conduct investigations, including fieldwork and fair experiments.",
              slos: [
                "Use a variety of investigation methods, including fieldwork, to collect data",
                "Select and use appropriate equipment for accurate and consistent data collection",
                "Identify the independent, dependent, and controlled variables in an investigation",
                "Manipulate and measure variables during the investigation",
                "Make and record clear and accurate observations",
                "Identify safety considerations and apply them while conducting the investigation",
                "Identify and mitigate any ethical dilemmas that may occur during data collection",
                "Collect enough data to gauge reliability of measurements",
                "Use preliminary trials to test and improve procedures"
              ]
            },
            {
              id: "1.3",
              title: "Processing and Analysing",
              description: "Use tables for the systematic recording of data and represent data in appropriate graphs; observe patterns and trends in the data.",
              slos: [
                "Construct appropriate tables to record collected data systematically",
                "Use the appropriate type of graph to display investigation data",
                "Outline trends or patterns in the data as shown in graphs",
                "Identify data that support the hypothesis or answer the question, and data that does not",
                "Summarise the data and attempt to explain patterns observed",
                "Write conclusions based on evidence from the investigation"
              ]
            },
            {
              id: "1.4",
              title: "Evaluating",
              description: "Suggest improvements to the methods of data collection to increase accuracy; evaluate the work of others.",
              slos: [
                "Recognise inconsistencies in data (outliers or unexpected results)",
                "Describe the difficulties in taking accurate and consistent measurements",
                "Summarise the problems encountered in collecting data, including external influences",
                "Suggest the use of different equipment for more accurate and consistent data collection",
                "Evaluate the reliability and validity of investigation methods"
              ]
            },
            {
              id: "1.5",
              title: "Communicating",
              description: "Use scientific literacy skills to communicate findings and ideas effectively.",
              slos: [
                "Use correct scientific terminology appropriately in explanations",
                "Use international conventions in presenting data (e.g., SI units, graphs and tables)",
                "Use different formats to present findings to an audience, including models, diagrams, digital presentations, and actions",
                "Present the results of an investigation to different communities and groups for consideration",
                "Recognise bias in communications and scientific reporting"
              ]
            }
          ]
        },
        {
          id: "strand2",
          title: "Science Understanding",
          description: "Understand key ideas of Science across the sub-strands, including facts, concepts, principles, laws, theories and models established by scientists over time.",
          majorLearningOutcome: "Students will understand key ideas of Science and the importance of Science in contemporary decision-making and problem-solving.",
          subStrands: [
            {
              id: "2.1",
              title: "Biological Science: Cells and Body Systems",
              description: "Investigate cells as the basic unit of living things; explore the structure and function of body systems and how they work together to maintain a healthy organism.",
              slos: [
                "Identify the cell as the basic unit of life and describe the functions of major cell structures (cell membrane, nucleus, cytoplasm, cell wall in plants)",
                "Compare and contrast plant and animal cells using diagrams and microscope observations",
                "Describe the levels of organisation: cells, tissues, organs, organ systems, organisms",
                "Identify the major organ systems in the human body (digestive, respiratory, circulatory, skeletal, muscular)",
                "Describe the structure and function of the digestive system and trace the path of food through the body",
                "Explain the function of the respiratory system and the process of gas exchange in the lungs",
                "Describe how the circulatory system transports nutrients, gases, and waste products around the body",
                "Explain how body systems work together to maintain health and how diseases can disrupt this",
                "Relate the importance of nutrition, exercise, and hygiene to the proper functioning of body systems in Vanuatu communities"
              ]
            },
            {
              id: "2.2",
              title: "Chemical Science: Chemical Reactions",
              description: "Investigate chemical changes and the evidence for chemical reactions; explore the properties of acids, bases, and indicators in everyday contexts.",
              slos: [
                "Distinguish between physical changes and chemical changes and provide examples of each",
                "Identify evidence that a chemical reaction has occurred (colour change, gas production, temperature change, precipitate formation)",
                "Describe everyday chemical reactions such as rusting, burning, cooking, and fermentation",
                "Define acids and bases and identify common examples found in Vanuatu (citrus fruits, vinegar, soap, coral lime)",
                "Use indicators (litmus paper, natural indicators) to classify substances as acidic, basic, or neutral",
                "Describe the pH scale and its relationship to acids, bases, and neutral substances",
                "Investigate neutralisation reactions and their practical applications (e.g., treating insect stings, soil improvement)",
                "Write simple word equations for common chemical reactions"
              ]
            },
            {
              id: "2.3",
              title: "Earth and Space Science: Plate Tectonics and Geological Events",
              description: "Investigate the structure of the Earth and the theory of plate tectonics; explore how geological events such as earthquakes and volcanoes affect Vanuatu and the Pacific region.",
              slos: [
                "Describe the internal structure of the Earth (crust, mantle, outer core, inner core)",
                "Explain the theory of plate tectonics and describe the three types of plate boundaries (convergent, divergent, transform)",
                "Explain how the movement of tectonic plates causes earthquakes and volcanic eruptions",
                "Identify Vanuatu's location on the Pacific Ring of Fire and explain why the region experiences frequent seismic and volcanic activity",
                "Describe the effects of earthquakes and volcanoes on communities and environments in Vanuatu",
                "Investigate methods for monitoring and predicting geological events",
                "Describe how tsunamis are generated and the safety measures communities in Vanuatu can take",
                "Explain the formation of different types of rocks (igneous, sedimentary, metamorphic) and the rock cycle",
                "Describe traditional Vanuatu knowledge and stories related to geological events"
              ]
            },
            {
              id: "2.4",
              title: "Physical Science: Energy Transfer and Transformation",
              description: "Investigate the different forms of energy and how energy is transferred and transformed; explore electrical circuits and energy use in everyday life.",
              slos: [
                "Identify the different forms of energy (kinetic, potential, thermal, light, sound, electrical, chemical)",
                "Describe how energy can be transferred from one object to another (e.g., conduction, convection, radiation)",
                "Explain the law of conservation of energy using everyday examples",
                "Describe energy transformations in common devices (e.g., torch: chemical to electrical to light)",
                "Investigate simple electrical circuits and identify series and parallel connections",
                "Describe the role of components in a circuit (battery, switch, resistor, bulb, wire)",
                "Measure voltage and current using appropriate instruments",
                "Explain the importance of energy conservation and the use of renewable energy sources in Vanuatu (solar, wind, hydro)",
                "Investigate traditional and modern methods of energy use in Vanuatu communities"
              ]
            }
          ]
        }
      ]
    },
    year9: {
      hours: 93,
      focus: "Understanding how organisms respond to environmental changes, chemical reactions and conservation of mass, plate tectonics, and properties of light and sound",
      strands: [
        {
          id: "strand1",
          title: "Science in Action",
          description: "Conduct scientific investigations using inquiry methodology, including collecting and analysing data; developing scientific explanations; using evidence to form defensible conclusions.",
          majorLearningOutcome: "Students will demonstrate understanding of how to conduct scientific investigations using inquiry methodology.",
          subStrands: [
            {
              id: "1.1",
              title: "Questioning and Predicting",
              description: "Formulate questions that can be investigated scientifically and make predictions based on scientific knowledge.",
              slos: [
                "Construct testable questions for descriptive/comparative investigations",
                "Identify local challenges that can be investigated scientifically",
                "Compose hypotheses from personal observations and other data for fair tests",
                "Formulate predictions using scientific reasoning for each investigation"
              ]
            },
            {
              id: "1.2",
              title: "Planning and Conducting",
              description: "Plan and conduct a range of investigation types to collect reliable data; identify and manipulate variables.",
              slos: [
                "Develop a plan for manipulation of independent, dependent and control variables",
                "Collect data using appropriate methods (counting, weighing, observing)",
                "Record data in appropriate format (tables, diagrams)",
                "Conduct trials to test planned data collection methods",
                "Identify factors that impact reliability of data",
                "Identify and explain how to mitigate safety concerns"
              ]
            },
            {
              id: "1.3",
              title: "Processing and Analysing",
              description: "Construct and use graphs, keys, and models to record and summarise data; analyse patterns and draw conclusions.",
              slos: [
                "Calculate average results from investigation data",
                "Construct appropriate graphs for average results",
                "Describe patterns and trends shown in graphs",
                "Discuss findings relating data, evidence and scientific knowledge",
                "Derive conclusions from hypotheses",
                "Formulate conclusions based on data and scientific knowledge"
              ]
            },
            {
              id: "1.4",
              title: "Evaluating",
              description: "Evaluate the quality of data collected; identify improvements to method.",
              slos: [
                "Identify unexpected or inconsistent results in data",
                "Explain unexpected or inconsistent results",
                "Suggest improvements to experimental design or procedure",
                "Use evidence-based conclusions to write follow-up investigation questions"
              ]
            },
            {
              id: "1.5",
              title: "Communicating",
              description: "Use appropriate scientific language, conventions, and representations to communicate findings.",
              slos: [
                "Use annotated models and diagrams to present scientific information",
                "Use SI units when representing measured data",
                "Describe scientific changes using word and mathematical equations",
                "Differentiate between common use of words and scientific meaning"
              ]
            }
          ]
        },
        {
          id: "strand2",
          title: "Science Understanding",
          description: "Understanding of how organisms respond to environmental changes, chemical reactions, plate tectonics, and properties of light and sound.",
          subStrands: [
            {
              id: "2.1",
              title: "Biological Science",
              description: "Organisms detect and respond to short-term and long-term environmental changes to survive and reproduce.",
              slos: [
                "Define environmental stimulus, biotic/abiotic factors",
                "List short-term and long-term environmental changes",
                "Explain importance of responding to environmental changes",
                "Identify receptors and sense organs organisms use to detect changes",
                "Differentiate between nerves and hormones",
                "Describe neuron structure and function (dendrite, cell body, axon)",
                "Explain the stimulus-response model",
                "Distinguish between reflex and reaction responses",
                "Define tropisms: phototropism, gravitropism, hydrotropism, thigmotropism, chemotropism, thermotropism",
                "Explain behavioural responses to daily and seasonal environmental changes",
                "Discuss physiological responses and structural adaptations",
                "Describe how hormones help animals time reproduction",
                "Describe coral spawning and environmental stimuli triggers"
              ]
            },
            {
              id: "2.2",
              title: "Chemical Science",
              description: "Chemical reactions involve rearranging atoms to form new substances; mass is conserved and energy is transferred.",
              slos: [
                "Distinguish between physical and chemical changes",
                "Identify reactants and products; write word equations",
                "State the law of conservation of mass (Lavoisier)",
                "Write symbols for the first 20 elements in the periodic table",
                "Describe atomic structure (protons, neutrons, electrons)",
                "Define atoms, molecules, elements and compounds",
                "Write chemical formulae and balanced equations",
                "Calculate atomic number, mass number, molar mass",
                "State Avogadro's number; calculate number of moles",
                "Distinguish between exothermic and endothermic reactions",
                "Describe photosynthesis, respiration, combustion and corrosion",
                "Discuss environmental impacts of combustion and economic impacts of corrosion"
              ]
            },
            {
              id: "2.3",
              title: "Earth and Space Science",
              description: "The Earth's crust is broken into large rocky plates that move, explaining geological activity and local features.",
              slos: [
                "Define plate tectonics; describe continental drift theory (Wegener)",
                "Describe evidence for plate tectonics (continental fit, fossil distribution)",
                "Name types of tectonic plates and main plates on a world map",
                "Identify types of plate movement (convergent, divergent, transform)",
                "Explain the cause of plate movements (convection currents)",
                "Explain how earthquakes occur at plate boundaries",
                "Describe seismograph function and Richter scale",
                "Explain how earthquakes cause tsunamis",
                "Name types of volcanoes; define Ring of Fire",
                "Explain Vanuatu's volcanic chain and geological risks",
                "Describe warning signs and safety instructions for geological events"
              ]
            },
            {
              id: "2.4",
              title: "Physical Science",
              description: "Nature and properties of light and sound energies; relate to the functioning of human eyes and ears.",
              slos: [
                "Define light; name natural/artificial sources",
                "Define electromagnetic spectrum, frequency and wavelength",
                "Explain light travels in straight lines; define transparent/translucent/opaque",
                "State the law of reflection; draw ray diagrams",
                "Identify plane, convex and concave mirrors and their images",
                "Define refraction; explain why light refracts between media",
                "Distinguish between convex and concave lenses",
                "Name parts of the human eye and explain image formation",
                "Explain refractive errors and corrections (short/long sightedness)",
                "Define sound; explain how sound is produced",
                "Describe sound wave properties (amplitude, wavelength, frequency)",
                "Explain sound energy transfer through media; why sound cannot travel through vacuum",
                "Name parts of the human ear; explain how sounds are detected",
                "Distinguish between infrasound, audible sound and ultrasound",
                "Explain echolocation, sonar, RADAR and ultrasound imaging"
              ]
            }
          ]
        }
      ],
      assessment: {
        internalExamination: { weighting: 60, description: "Two-hour written exam per term" },
        continuousAssessment: { weighting: 40, description: "Teacher Design Tasks (TDT) + Common Assessment Task (CAT)" }
      },
      units: [
        {
          id: "9.1",
          name: "Biological Science - Organisms and Environment",
          hours: 20,
          theme: "C",
          objectives: [
            "Identify environmental stimuli and organism responses",
            "Explain nervous system and stimulus-response model",
            "Describe plant tropisms and animal behavioural responses",
            "Discuss seasonal and daily responses in organisms"
          ],
          content: [
            "Environmental stimuli (biotic and abiotic)",
            "Receptors and sense organs",
            "Neurons and nervous system",
            "Stimulus-response model",
            "Plant tropisms (photo, gravi, hydro, thigmo, chemo, thermo)",
            "Behavioural and physiological responses to environment"
          ],
          activities: [
            "Plant tropism experiments",
            "Reflex testing activities",
            "Animal response case studies"
          ]
        },
        {
          id: "9.2",
          name: "Chemical Science - Reactions and Atoms",
          hours: 25,
          theme: "B",
          objectives: [
            "Distinguish physical and chemical changes",
            "Describe atomic structure and the periodic table",
            "Write and balance chemical equations",
            "Understand conservation of mass and energy transfer in reactions"
          ],
          content: [
            "Physical vs chemical changes",
            "Atomic structure and subatomic particles",
            "Periodic table - first 20 elements",
            "Chemical formulae and balanced equations",
            "Molar mass and mole concept",
            "Exothermic and endothermic reactions",
            "Photosynthesis, respiration, combustion, corrosion"
          ],
          activities: [
            "Chemical reaction demonstrations",
            "Building atomic models",
            "Balancing equation exercises"
          ]
        },
        {
          id: "9.3",
          name: "Earth and Space Science - Plate Tectonics",
          hours: 18,
          theme: "D",
          objectives: [
            "Describe plate tectonics theory and continental drift",
            "Explain earthquakes, volcanoes and tsunamis",
            "Relate Vanuatu's geology to plate tectonics",
            "Describe geological safety procedures"
          ],
          content: [
            "Continental drift and plate tectonics theory",
            "Plate types and movements",
            "Earthquakes - seismographs and Richter scale",
            "Volcanoes and Ring of Fire",
            "Vanuatu's geological features and risks",
            "Tsunami generation and safety"
          ],
          activities: [
            "Plate boundary simulations",
            "Building seismograph models",
            "Vanuatu case studies"
          ]
        },
        {
          id: "9.4",
          name: "Physical Science - Light and Sound",
          hours: 25,
          theme: "A",
          objectives: [
            "Understand properties of light including reflection and refraction",
            "Explain how the human eye works and vision correction",
            "Understand properties of sound waves",
            "Explain how the human ear works and sound technology"
          ],
          content: [
            "Light properties, electromagnetic spectrum",
            "Reflection - law, mirrors (plane, convex, concave)",
            "Refraction - lenses (convex, concave)",
            "Human eye structure and vision correction",
            "Sound production, waves, properties",
            "Human ear structure and hearing",
            "Echolocation, sonar, RADAR, ultrasound"
          ],
          activities: [
            "Ray diagram construction",
            "Lens and mirror experiments",
            "Sound wave investigations",
            "Musical instrument creation"
          ]
        }
      ]
    },
    year10: {
      hours: 132,
      focus: "Genetics and inheritance, types of chemical reactions, climate change and Earth systems, motion and safety with simple machines",
      strands: [
        {
          id: "strand1",
          title: "Science in Action",
          description: "Conduct scientific investigations using inquiry methodology; collect and analyse data; develop scientific explanations; use evidence to form defensible conclusions and provide solutions to real-world problems.",
          majorLearningOutcome: "Students will demonstrate understanding of scientific investigation methodology and evidence-based reasoning.",
          subStrands: [
            {
              id: "1.1",
              title: "Questioning and Predicting",
              description: "Identify factors for investigation, formulate testable questions and hypotheses, make predictions using scientific reasoning.",
              slos: [
                "Write testable questions appropriate for experimental investigations",
                "Explain how a scientific question, hypothesis, and prediction are connected",
                "Compose a hypothesis from primary/secondary sources",
                "Formulate predictions using scientific reasoning",
                "Evaluate the quality of a scientific question/hypothesis"
              ]
            },
            {
              id: "1.2",
              title: "Planning and Conducting",
              description: "Plan investigations with consideration of variables, reliability, safety and ethics.",
              slos: [
                "Explain the choice of variables to be tested",
                "Explain the choice of control used in the investigation",
                "Describe how the reliability of data collection is maximised",
                "Explain safety and ethical issues in planning"
              ]
            },
            {
              id: "1.3",
              title: "Processing and Analysing",
              description: "Record, present and analyse data; identify trends and relationships.",
              slos: [
                "Select appropriate means for recording data",
                "Describe relationships between variables using formulae and descriptions",
                "Explain how inconsistencies in data might have occurred",
                "Compare conclusions with scientific knowledge from other sources"
              ]
            },
            {
              id: "1.4",
              title: "Evaluating",
              description: "Evaluate data quality, validity and suggest improvements.",
              slos: [
                "Describe specific ways to improve data quality",
                "Analyse the validity of data in drawing conclusions",
                "Evaluate the effectiveness of the experimental investigation",
                "Identify biases and uncertainties in data",
                "Suggest alternative explanations based on data"
              ]
            },
            {
              id: "1.5",
              title: "Communicating",
              description: "Use scientific terminology, conventions and representations to communicate findings.",
              slos: [
                "Use correct scientific terminology in presentations",
                "Use internationally recognised conventions for graphs and formulae",
                "Construct arguments supported by scientifically valid evidence"
              ]
            }
          ]
        },
        {
          id: "strand2",
          title: "Science Understanding",
          description: "Understanding genetics and inheritance, chemical reactions and the periodic table, climate change and Earth systems, and motion and safety.",
          subStrands: [
            {
              id: "2.1",
              title: "Biological Science: Genetics and Inheritance",
              description: "Patterns of genetic inheritance, the role of DNA in determining traits, reproductive processes that produce variations, and applications to agriculture.",
              slos: [
                "Define inherited traits; identify similarities between parents and offspring",
                "Define gene, DNA, chromosome; state their relationships",
                "Describe DNA structure and DNA replication",
                "Differentiate between mitosis and meiosis",
                "Describe the structure of a nucleotide and the four nitrogenous bases (A, T, G, C)",
                "Explain how genetic information is encoded and can be altered (mutated)",
                "Define allele; explain dominant/recessive, homozygous/heterozygous",
                "Define genotype and phenotype; set up Punnett squares for monohybrid crosses",
                "Discuss probabilities of genotypes and phenotypes",
                "Define sex chromosomes and sex-linked traits (haemophilia, colour blindness)",
                "Describe autosomal dominant/recessive and x-linked inheritance patterns",
                "Use family trees to determine probable inheritance patterns",
                "Define selective breeding; describe applications to agriculture",
                "Define hybrid vigour/heterosis and genetically modified organisms",
                "Describe genetic modification examples (pawpaw, Enviropig, sickle cell treatment)"
              ]
            },
            {
              id: "2.2",
              title: "Chemical Science: Types of Chemical Reactions",
              description: "Atomic structure, periodic table organisation, chemical bonding, reaction types and rates of reaction.",
              slos: [
                "Write electron configuration of first 20 elements",
                "Locate elements by group/row; identify metals, non-metals, noble gases, halogens",
                "Identify valency and energy levels from periodic table position",
                "Describe the octet rule; define ions, cations and anions",
                "Describe how ionic and covalent bonds are formed",
                "List examples of ionic compounds (Group 1+7, Group 2+6) and covalent compounds",
                "Define activity series; write reactivity order for common cations",
                "Use activity series to predict metal displacement reactions",
                "Define precipitation reactions; use solubility rules to predict precipitates",
                "Define rate of reaction; describe methods to measure it (gas volume, mass, concentration, colour, pH)",
                "Describe collision theory",
                "Explain effects of concentration, surface area, and temperature on reaction rate",
                "Apply collision theory to real-life examples"
              ]
            },
            {
              id: "2.3",
              title: "Earth and Space Science: Climate Change",
              description: "Interactions between Earth's four spheres, natural cycles, weather phenomena, greenhouse effect, global warming and climate change impacts on Vanuatu.",
              slos: [
                "Define and identify Earth's four spheres (biosphere, geosphere, hydrosphere, atmosphere)",
                "Describe the main stages of water, carbon, nitrogen and oxygen cycles",
                "Explain how materials circulate between spheres",
                "Explain formation of wind, rain and cyclones; relate to weather conditions",
                "Read and interpret simple weather documents",
                "Describe composition of atmospheric air; define greenhouse effect",
                "List main greenhouse gases; explain how increases change the greenhouse effect",
                "Define biodiversity and global warming; identify causes and consequences",
                "Explain how global warming alters biodiversity distribution",
                "Describe effects of global warming on Vanuatu specifically",
                "Define climate change; identify current evidence and contributing factors",
                "Describe ways to reduce local and global climate change factors",
                "Explain the Paris Agreement and its objectives",
                "Describe Vanuatu's main vulnerabilities (Ring of Fire, cyclone belt, sea level rise)"
              ]
            },
            {
              id: "2.4",
              title: "Physical Science: Motion and Safety",
              description: "Simple machines, laws of motion, forces, momentum and vehicle safety applications.",
              slos: [
                "Define simple machines; identify types (lever, wheel-axle, pulley, inclined plane, screw, wedge)",
                "Describe parts of a lever; explain how each simple machine makes work easier",
                "Define mechanical advantage; explain force vs distance advantage",
                "Define work (W = F × d); explain the unit Joule",
                "Define machine efficiency; calculate using Efficiency = Useful work/Work supplied × 100",
                "Define distance, displacement, average speed, average velocity, acceleration",
                "Compare scalar vs vector quantities",
                "Define Newton's first law (inertia); explain friction and air resistance",
                "State and apply Newton's second law (F = m × a)",
                "Define momentum (p = m × v); explain role in vehicle movement",
                "Explain braking distance and why it increases with speed",
                "List and explain safety features of modern vehicles",
                "Connect each safety feature to relevant Newton's law",
                "List road safety rules"
              ]
            }
          ]
        }
      ],
      assessment: {
        externalExamination: { weighting: 60, description: "Two-hour written examination" },
        internalAssessment: { weighting: 40, description: "Teacher Design Tasks (TDT) + Common Assessment Task (CAT)" }
      },
      units: [
        {
          id: "10.1",
          name: "Biological Science - Genetics and Inheritance",
          hours: 35,
          theme: "C",
          objectives: [
            "Understand patterns of genetic inheritance and role of DNA",
            "Describe cell division, DNA replication and mutation",
            "Use Punnett squares for monohybrid crosses",
            "Describe applications of genetics to agriculture"
          ],
          content: [
            "Inherited traits in humans, animals and plants",
            "DNA structure, replication, genes, chromosomes",
            "Mitosis vs meiosis",
            "Alleles, genotype, phenotype, Punnett squares",
            "Sex-linked traits and inheritance patterns",
            "Selective breeding, hybrid vigour, GMOs"
          ],
          activities: [
            "DNA model building",
            "Punnett square exercises",
            "Family tree analysis",
            "Selective breeding case studies"
          ]
        },
        {
          id: "10.2",
          name: "Chemical Science - Types of Chemical Reactions",
          hours: 30,
          theme: "B",
          objectives: [
            "Understand atomic structure and periodic table organisation",
            "Describe ionic and covalent bonding",
            "Predict products of displacement and precipitation reactions",
            "Explain factors affecting rates of reaction"
          ],
          content: [
            "Electron configuration of first 20 elements",
            "Periodic table groups and properties",
            "Ionic and covalent bonding",
            "Activity series and displacement reactions",
            "Precipitation reactions and solubility rules",
            "Rate of reaction and collision theory"
          ],
          activities: [
            "Periodic table exploration activities",
            "Bonding model building",
            "Displacement reaction experiments",
            "Rate of reaction investigations"
          ]
        },
        {
          id: "10.3",
          name: "Earth and Space Science - Climate Change",
          hours: 35,
          theme: "D",
          objectives: [
            "Understand interactions between Earth's four spheres",
            "Explain natural cycles and weather phenomena",
            "Analyse greenhouse effect and climate change",
            "Evaluate impacts on Vanuatu and mitigation strategies"
          ],
          content: [
            "Earth's four spheres and their interactions",
            "Water, carbon, nitrogen and oxygen cycles",
            "Weather formation and cyclones",
            "Greenhouse effect and global warming",
            "Climate change evidence and impacts",
            "Vanuatu vulnerabilities and Paris Agreement"
          ],
          activities: [
            "Natural cycle diagram creation",
            "Weather data analysis",
            "Climate change data interpretation",
            "Vanuatu impact case studies"
          ]
        },
        {
          id: "10.4",
          name: "Physical Science - Motion and Safety",
          hours: 32,
          theme: "A",
          objectives: [
            "Understand simple machines and mechanical advantage",
            "Apply Newton's laws of motion",
            "Calculate work, efficiency, momentum",
            "Relate physics concepts to vehicle and workplace safety"
          ],
          content: [
            "Six types of simple machines",
            "Mechanical advantage, work and efficiency",
            "Distance, displacement, speed, velocity, acceleration",
            "Newton's three laws of motion",
            "Momentum, braking distance",
            "Vehicle safety features and road safety"
          ],
          activities: [
            "Simple machine demonstrations",
            "Force and motion experiments",
            "Vehicle safety research project",
            "Newton's law applications"
          ]
        }
      ]
    }
  }
};

export default juniorScienceCurriculum;
